import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { isNil, isEmpty } from 'lodash'
import FormikTextField from '@ui-components-3/compat/lib/components/FormikTextField'
import FormikSelect from '@ui-components-3/compat/lib/components/FormikSelect'
import FormControl from '@ui-components-3/ui/lib/components/FormControl'
import Checkbox from '@ui-components-3/ui/lib/components/Checkbox'
import Loading from '@ui-components-3/ui/lib/components/Loading'
import SearchIcon from '@ui-components-3/icons/lib/regular/MagnifyingGlass'
import classNames from '@ui-components-3/ui/lib/utils/classNames'

import withForm from '../../../hoc/withForm'
import TestIds from '../../../util/TestIds'
import { distanceOptions, specialtyOptions } from '../../../util/pharmacies'
import PharmacySearchListItem from '../PharmacySearchListItem'
import { Divider } from '@material-ui/core'
import { useGeolocated } from 'react-geolocated'
import States from '../../../util/states'

const getOptionLabel = (g) => g.label
const getOptionValue = (g) => g.value
const isOptionDisabled = (o) => o.disabled

const PharmacySearch = (props) => {
  const { handleSubmit, setFieldValue, addPharmacy, searchResults, values, initialValues } = props
  const { isGeolocationEnabled, isGeolocationAvailable, coords } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: false,
    },
  })
  const [isUsingLocation, setIsUsingLocation] = useState(false)
  const [canUseLocation, setCanUseLocation] = useState(false)
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  useEffect(() => {
    setCanUseLocation(isGeolocationAvailable && isGeolocationEnabled && !isNil(coords))
    if (isUsingLocation) setIsUsingLocation(isGeolocationAvailable && isGeolocationEnabled)
    setIsLoadingLocation(isGeolocationAvailable && isGeolocationEnabled ? isNil(coords) : false)
  }, [isGeolocationAvailable, isGeolocationEnabled, coords])

  const handleCheck = useCallback(
    (event) => {
      const nextValue = !isNil(coords) && event.target.checked
      setIsUsingLocation(nextValue)
      if (nextValue) {
        setFieldValue('lat', coords.latitude)
        setFieldValue('lng', coords.longitude)
        setFieldValue('city', '')
        setFieldValue('state', '')
        setFieldValue('zip', '')
      } else {
        setFieldValue('city', initialValues.city)
        setFieldValue('state', initialValues.state)
        setFieldValue('lat', '')
        setFieldValue('distance', '')
        setFieldValue('lng', '')
      }
    },
    [coords],
  )

  const hasSomeFilter = useMemo(() => {
    return Object.values(values).reduce((acc, value) => {
      return acc || (!!value && !isEmpty(value))
    }, false)
  }, [values])

  return (
    <form
      onSubmit={handleSubmit}
      className="w-[570px] max-w-full"
      data-testid={TestIds.newVisit.view.pharmacySearchDialog}
    >
      <div className={classNames(!!searchResults?.length && 'hidden')}>
        <div className="mb-4 grid grid-cols-1 gap-y-4 px-4 pt-2 md:px-6">
          <FormikTextField
            fullWidth
            autoComplete="off"
            label={'Pharmacy Name'}
            data-testid={TestIds.newVisit.input.pharmacyName}
            helper={'Optional'}
            name={'storeName'}
          />
          <FormikSelect
            multiple
            data-testid={TestIds.newVisit.input.pharmacySpecialty}
            label={'Specialty'}
            name={'specialty'}
            helperText={'Optional'}
            options={specialtyOptions}
            isOptionDisabled={isOptionDisabled}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
          />
          <FormikTextField
            fullWidth
            autoComplete="address-line1"
            label={'City'}
            data-testid={TestIds.newVisit.input.pharmacyCity}
            name={'city'}
          />
          <FormikTextField
            fullWidth
            autoComplete="postal-code"
            label={'Zipcode'}
            data-testid={TestIds.newVisit.input.pharmacyZip}
            name={'zip'}
          />
          <FormikSelect
            label={'State'}
            data-testid={TestIds.newVisit.input.pharmacyState}
            name={'state'}
            clearable
            options={States}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
          />
          <FormControl
            label={
              <div
                className={classNames('flex items-center', canUseLocation ? 'text-neutral-700' : 'text-neutral-500')}
              >
                Use my location
                {!!isLoadingLocation && <Loading className="py-0" />}
              </div>
            }
            labelPlacement="right"
            data-testid={TestIds.newVisit.input.pharmacyLocation}
          >
            <Checkbox disabled={!canUseLocation} checked={isUsingLocation} onChange={handleCheck} />
          </FormControl>
          {!!isUsingLocation && (
            <FormikSelect
              data-testid={TestIds.newVisit.input.pharmacyDistance}
              label={'Distance'}
              name={'distance'}
              helperText={'Optional'}
              options={distanceOptions}
              isOptionDisabled={isOptionDisabled}
              getOptionLabel={getOptionLabel}
              getOptionValue={getOptionValue}
            />
          )}
        </div>
        <div className="border-t border-t-neutral-300 bg-neutral-900 px-4 pb-6 pt-4 md:px-6">
          <button
            data-testid={TestIds.newVisit.button.searchPharmacy}
            disabled={!hasSomeFilter}
            className="btn btn-primary w-full"
            type="submit"
          >
            <SearchIcon className="h-[20px] w-[20px]" aria-hidden="true" />
            Search Pharmacy
          </button>
        </div>
      </div>
      {!!searchResults?.length && (
        <div className="px-4 pb-4 md:px-6">
          {searchResults.map((pharmacy, i) => {
            return (
              <Fragment key={pharmacy.id}>
                <Divider />
                <PharmacySearchListItem
                  pharmacy={pharmacy}
                  index={i}
                  onAddClick={addPharmacy(pharmacy)}
                  data-testid={TestIds.newVisit.input.pharmacySearch(i)}
                />
              </Fragment>
            )
          })}
        </div>
      )}
    </form>
  )
}

PharmacySearch.propTypes = {
  searchResults: PropTypes.array,
  initialValues: PropTypes.object.isRequired,
  schema: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  enableReinitialize: PropTypes.bool,
  dirty: PropTypes.bool,
  isValid: PropTypes.bool,
  isLoading: PropTypes.bool,
  addPharmacy: PropTypes.func.isRequired,
}

export default withForm(PharmacySearch)
